import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for WebSocket connection
 * Handles authentication, reconnection, and message handling
 */
export const useWebSocket = (userType = 'dispatcher', onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const pingInterval = useRef(null);
  // Store onMessage in a ref so it never causes connect() to be recreated
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, cannot connect to WebSocket');
        return;
      }

      // Clean up existing connection
      if (ws.current && ws.current.readyState !== WebSocket.CLOSED) {
        ws.current.close();
      }

      // Determine WebSocket URL based on environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = backendUrl.replace('https://', '').replace('http://', '');
      const wsUrl = `${wsProtocol}://${wsHost}/ws/${userType}`;

      console.log('Connecting to WebSocket:', wsUrl);
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected, sending auth...');
        // Send authentication message
        ws.current.send(JSON.stringify({ token }));
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'authenticated') {
            console.log('WebSocket authenticated:', data);
            setIsConnected(true);
            reconnectAttempts.current = 0;
            
            // Start ping interval to keep connection alive
            if (pingInterval.current) clearInterval(pingInterval.current);
            pingInterval.current = setInterval(() => {
              if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                ws.current.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000);
            return;
          }
          
          if (data.type === 'pong') {
            return; // Ignore pong responses
          }
          
          if (data.error) {
            console.error('WebSocket error message:', data.error);
            return;
          }

          console.log('WebSocket message received:', data.type);
          setLastMessage(data);
          
          // Call the onMessage callback if provided
          if (onMessageRef.current && typeof onMessageRef.current === 'function') {
            onMessageRef.current(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }

        // Attempt to reconnect if not a normal closure and we have a token
        const token = localStorage.getItem('token');
        if (token && event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [userType]); // onMessage intentionally excluded — stored in ref above

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    if (ws.current) {
      ws.current.close(1000, 'Component unmounted');
      ws.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket is not connected, attempting reconnect...');
      connect();
      return false;
    }
  }, [connect]);

  useEffect(() => {
    // Only connect if we have a token
    const token = localStorage.getItem('token');
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect: connect,
    disconnect
  };
};

export default useWebSocket;
